import { QueryClientProvider } from '@tanstack/react-query'
import { type IndexRouteObject, Navigate, type NonIndexRouteObject, Outlet } from 'react-router'

import { ApiProvider, Auth0UserProvider } from '@fortem-monorepo/react-spa'
import { LoadingScreen } from '@fortem-monorepo/react-ui'

import { PERMISSIONS } from '@/constants/permissions'
import { FortemLayout } from '@/layout'
import { queryClient } from '@/lib/query-client'
import { DashboardPage } from '@/pages/_dashboard/page'
import { accountRoutes } from '@/pages/account/routes'
import { adminRoutes } from '@/pages/admin/routes'
import { RouteErrorPage } from '@/pages/error'
import { feedsRoutes } from '@/pages/feeds/routes'
import { flowRoutes } from '@/pages/flow/routes'
import { fundsRoutes } from '@/pages/funds/routes'
import { intsrumentRoutes } from '@/pages/instruments/routes'
import { monitoringRoutes } from '@/pages/monitoring/routes'
import { operationsRoutes } from '@/pages/operations/routes'
import { pmRoutes } from '@/pages/pm/routes'
import { salesRoutes } from '@/pages/sales/routes'
import { smartradeRoutes } from '@/pages/smartrade/routes'
import { tradingRoutes } from '@/pages/trading/routes'
import { useInternalUser } from '@/react-query/queries/account'

type WithHandle<H> = { handle?: H }

export type CustomRouteObject<H = any> =
  | (IndexRouteObject & WithHandle<H> & { children?: CustomRouteObject<H>[] })
  | (NonIndexRouteObject & WithHandle<H> & { children?: CustomRouteObject<H>[] })

export type FortemRouteHandle = {
  title?: string
  docLink?: string
}

export const router = [
  {
    errorElement: <RouteErrorPage />,
    element: (
      <QueryClientProvider client={queryClient}>
        <ApiProvider clientOptions={{ baseUrl: import.meta.env.VITE_ROOT_API_URL }}>
          <Auth0UserProvider
            permissions={PERMISSIONS}
            usePermissions={() => {
              const { query, internalUser, permissions } = useInternalUser()

              return {
                isLoading: query.isLoading || !internalUser,
                permissions,
              }
            }}
            loadingFallback={
              <LoadingScreen>
                <span className={'text-sm tracking-wide uppercase'}>Loading Portal</span>
              </LoadingScreen>
            }
          >
            <Outlet />
          </Auth0UserProvider>
        </ApiProvider>
      </QueryClientProvider>
    ),
    children: [
      {
        path: '/',
        element: <FortemLayout />,
        children: [
          {
            index: true,
            element: <DashboardPage />,
            handle: {
              title: 'Dashboard',
            },
          },
          ...adminRoutes,
          ...feedsRoutes,
          ...fundsRoutes,
          ...monitoringRoutes,
          ...operationsRoutes,
          ...flowRoutes,
          ...tradingRoutes,
          ...accountRoutes,
          ...intsrumentRoutes,
          ...salesRoutes,
          ...smartradeRoutes,
          ...pmRoutes,
        ],
      },
      {
        path: '*',
        element: <Navigate to="/" />,
      },
    ],
  },
] satisfies CustomRouteObject<FortemRouteHandle>[]
